import axios from 'axios'
import qs from 'qs'
import request from '../helpers/authorizateRequest'
import {
    positiveId,
    rethrowBlobError,
    timelineKeys,
    timelineParams,
    validateAttachmentResponse,
} from './operatorMonitoring'

// Second generation of the operator monitoring API: one row per global user,
// a shared company scope and company-qualified chat resources. Every scoped
// request carries the scope version the screen was loaded with so the backend
// can answer 409 `scope_changed` when another admin edits the company set.
const BASE_URL = '/operator-monitoring/v2'

function get(path, params = {}, cancelToken = null, extra = {}) {
    return request.get(`${BASE_URL}${path}`, { params, cancelToken, ...extra }).then(response => response.data)
}

function chatPath(companyId, chatId) {
    return `/chats/${positiveId(companyId, 'company_id')}/${positiveId(chatId, 'chat_id')}`
}

function withScope(scope, params = {}) {
    const scoped = { ...params, scope_version: scope.version }
    if (scope.id !== null && scope.id !== undefined) scoped.scope_id = scope.id
    return scoped
}

export default {
    createCancelSource() {
        return axios.CancelToken.source()
    },

    isCancel(error) {
        return axios.isCancel(error)
    },

    scope(cancelToken) {
        return get('/scope', {}, cancelToken)
    },

    availableCompanies(cancelToken) {
        return get('/scope/available-companies', {}, cancelToken)
    },

    updateScope(body, cancelToken) {
        return request.put(`${BASE_URL}/scope`, body, { cancelToken }).then(response => response.data)
    },

    reference(cancelToken) {
        return get('/reference', {}, cancelToken)
    },

    operators(scope, params, cancelToken) {
        return get('/operators', withScope(scope, params), cancelToken)
    },

    operator(scope, userId, cancelToken) {
        return get(`/operators/${positiveId(userId, 'user_id')}`, withScope(scope), cancelToken)
    },

    operatorChats(scope, userId, params, cancelToken) {
        return get(`/operators/${positiveId(userId, 'user_id')}/chats`, withScope(scope, params), cancelToken)
    },

    chat(scope, companyId, chatId, cancelToken) {
        return get(chatPath(companyId, chatId), withScope(scope), cancelToken)
    },

    chatMessages(scope, companyId, chatId, params, cancelToken) {
        return get(`${chatPath(companyId, chatId)}/messages`, withScope(scope, timelineParams(params)), cancelToken)
    },

    chatItems(scope, companyId, chatId, keys, cancelToken) {
        return get(`${chatPath(companyId, chatId)}/items`, withScope(scope, { keys: timelineKeys(keys) }), cancelToken, {
            paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets' }),
        })
    },

    downloadAttachment(scope, companyId, chatId, sourceMessageId, cancelToken) {
        const messageId = positiveId(sourceMessageId, 'source_message_id')
        return request.get(`${BASE_URL}${chatPath(companyId, chatId)}/messages/${messageId}/file`, {
            params: withScope(scope),
            cancelToken,
            responseType: 'blob',
        }).then(validateAttachmentResponse, rethrowBlobError)
    },

    reportOperators(scope, params, cancelToken) {
        return get('/reports/operators', withScope(scope, params), cancelToken)
    },

    activityConfig(cancelToken) {
        return get('/activity/config', {}, cancelToken)
    },

    heartbeat(body, cancelToken) {
        return request.post(`${BASE_URL}/activity/heartbeat`, body, { cancelToken }).then(response => response.data)
    },
}
